import { useEffect, useRef } from 'react';
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
import type { ArcgisMapView } from './arcgisView';
import type { ClassBreaksRendererJson } from './outageLayerStyles';

export type FieldInfoJson = {
  fieldName: string;
  label: string;
  format?: { places?: number; digitSeparator?: boolean; dateFormat?: string };
};

/**
 * Shared lifecycle for the county choropleth GeoJSONLayers (power outages,
 * ERI): serialize the FeatureCollection to a Blob URL, build a GeoJSONLayer
 * with a class-breaks renderer + fieldInfos popup, add it beneath operational
 * layers, and tear it down (revoking the blob) when the data changes or the map
 * unmounts. Visibility toggles do not rebuild.
 */
export function useCountyChoroplethLayerSync({
  mapView,
  layerId,
  title,
  isVisible,
  featureCollection,
  renderer,
  popupTitle,
  fieldInfos,
}: {
  mapView: ArcgisMapView | null;
  layerId: string;
  title: string;
  isVisible: boolean;
  /** Undefined/empty removes the layer. */
  featureCollection:
    | { type: 'FeatureCollection'; features: unknown[] }
    | undefined;
  renderer: ClassBreaksRendererJson;
  popupTitle: string;
  fieldInfos: FieldInfoJson[];
}) {
  const layerRef = useRef<GeoJSONLayer | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!mapView?.map) return;
    const map = mapView.map;

    const teardown = () => {
      if (layerRef.current) {
        map.remove(layerRef.current);
        layerRef.current.destroy();
        layerRef.current = null;
      }
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };

    teardown();

    if (!featureCollection || featureCollection.features.length === 0) {
      return;
    }

    const blob = new Blob([JSON.stringify(featureCollection)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    urlRef.current = url;

    const layer = new GeoJSONLayer({
      id: layerId,
      title,
      url,
      visible: isVisible,
      // Autocast from JSON; keeps this hook free of renderer/symbol class imports.
      renderer: renderer as unknown as __esri.ClassBreaksRendererProperties & {
        type: 'class-breaks';
      },
      popupTemplate: {
        title: popupTitle,
        content: [{ type: 'fields', fieldInfos }],
      },
    });

    map.add(layer);
    // Below operational layers so points/AORs draw on top of the choropleth.
    map.reorder(layer, 0);
    layerRef.current = layer;

    return teardown;
    // `isVisible` is applied in the effect below so toggling does not rebuild the layer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mapView,
    layerId,
    title,
    featureCollection,
    renderer,
    popupTitle,
    fieldInfos,
  ]);

  useEffect(() => {
    if (layerRef.current) layerRef.current.visible = isVisible;
  }, [isVisible]);
}
